import Link from 'next/link'
import React from 'react'
import styled from 'styled-components'
import useMedia from 'use-media'

import theme from '../../theme'
import { DESKTOP_WIDTH } from '../../utils/Media'

interface Props {
  sum: number
  per: number
  baseUrl: string
  currentPage: number
  addParam: string
}

const Pagination: React.FC<Props> = ({
  sum,
  per,
  baseUrl,
  currentPage,
  addParam,
}) => {
  const isMobileTablet = useMedia({ maxWidth: DESKTOP_WIDTH })

  const smoothScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  // 前・後に表示する最大ページ数
  const displayCount = isMobileTablet ? 1 : 4

  // 全ページ数
  const totalPage: number = Math.ceil(sum / per)

  // 前に表示するページ数
  const afterCount = [...Array(totalPage).keys()].filter((index) => {
    const page = index + 1 // indexをpageに変換
    return currentPage < page
  }).length

  // 後に表示するページ数
  const prevCount = [...Array(totalPage).keys()].filter((index) => {
    const page = index + 1 // indexをpageに変換
    return page < currentPage
  }).length

  return (
    <div>
      {/* ページ番号が0（= アイテムが0個）のときは何も描画しない */}
      {totalPage !== 1 && (
        <Pages>
          {currentPage !== 1 && (
            <StyledSpan>
              <BtnPrevNext
                href={`${baseUrl}1${addParam}`}
                scroll={false}
                onClick={smoothScrollToTop}
                passHref
              >
                ＜
              </BtnPrevNext>
            </StyledSpan>
          )}

          {prevCount > displayCount && <StyledSpan>...</StyledSpan>}

          {[...Array(totalPage).keys()]
            .filter((index) => {
              const page = index + 1 // indexをpageに変換
              return (
                page <= currentPage + displayCount &&
                currentPage - displayCount <= page
              )
            })
            .map((page) => {
              const nextPage = page + 1
              const target = baseUrl + nextPage.toString() + addParam
              return nextPage === currentPage ? (
                <ActivePage key={nextPage}>
                  <ActivePageSpan>{nextPage}</ActivePageSpan>
                </ActivePage>
              ) : (
                <Page key={nextPage}>
                  <PageAnchor
                    href={target}
                    onClick={smoothScrollToTop}
                    scroll={false}
                    passHref
                  >
                    {nextPage}
                  </PageAnchor>
                </Page>
              )
            })}

          {afterCount > displayCount && <StyledSpan>...</StyledSpan>}

          {currentPage !== totalPage && (
            <StyledLastList>
              <BtnPrevNext
                href={`${baseUrl}${totalPage.toString()}${addParam}`}
                scroll={false}
                onClick={smoothScrollToTop}
                passHref
              >
                ＞
              </BtnPrevNext>
            </StyledLastList>
          )}
        </Pages>
      )}
    </div>
  )
}

const ActivePageSpan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding-left: 5px;
  border-radius: 50% 50%;
  background-color: ${theme.colors.primary};
  color: #fff;
  font-family:
    Highway Gothic Wide,
    sans-serif;
  text-decoration: none;
`

const PageAnchor = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding-left: 5px;
  border: 1px solid #000;
  border-radius: 50% 50%;

  color: #000;
  font-family:
    Highway Gothic Wide,
    sans-serif;
  text-decoration: none;

  &:hover {
    border: 3px solid ${theme.colors.primary};
    background-color: #fff;
    color: ${theme.colors.primary};
  }
`

const BtnPrevNext = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding-left: 5px;
  border: 3px solid ${theme.colors.primary};
  border-radius: 50% 50%;
  background-color: ${theme.colors.primary};
  color: #fff;
  text-decoration: none;

  &:hover {
    background-color: #fff;
    color: ${theme.colors.primary};
  }
`

const StyledSpan = styled.li`
  margin-right: 30px;
  line-height: 44px;
  cursor: pointer;
`

const StyledLastList = styled.li`
  line-height: 27px;
  cursor: pointer;
`

const Pages = styled.ul`
  display: flex;
  justify-content: center;
`

const ActivePage = styled.li`
  margin-right: 30px;
  font-size: 20px;
  line-height: 25px;
`

const Page = styled.li`
  margin-right: 30px;
  font-size: 20px;
  line-height: 25px;
  cursor: pointer;
`

export default Pagination
